import type { SubtitleCue } from '@/types';
import { parseSRT } from '@/lib/utils';

/** Convert VTT timestamp string to seconds */
function vttTimeToSeconds(ts: string): number {
  const parts = ts.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return parts[0] * 60 + (parts[1] ?? 0);
}

/** Parse a WebVTT string into subtitle cues */
export function parseVTT(vtt: string): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  const blocks = vtt
    .replace(/\r\n/g, '\n')
    .split('\n\n')
    .filter((b) => b.trim() && !b.startsWith('WEBVTT'));

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    const timingIdx = lines.findIndex((l) => l.includes('-->'));
    if (timingIdx === -1) continue;

    const timingLine = lines[timingIdx];
    const match = timingLine.match(
      /(\d{1,2}:\d{2}:\d{2}\.\d{3}|\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}\.\d{3}|\d{2}:\d{2}\.\d{3})/
    );
    if (!match) continue;

    const startTime = vttTimeToSeconds(match[1]);
    const endTime = vttTimeToSeconds(match[2]);
    const text = lines
      .slice(timingIdx + 1)
      .join('\n')
      .replace(/<[^>]+>/g, '')
      .trim();

    if (text) {
      cues.push({
        id: `${startTime}-${endTime}`,
        startTime,
        endTime,
        text,
      });
    }
  }

  return cues;
}

/** Load and parse a subtitle file (SRT or VTT) */
export async function loadSubtitleFile(file: File): Promise<SubtitleCue[]> {
  const text = await file.text();
  const lowerName = file.name.toLowerCase();

  if (lowerName.endsWith('.vtt')) {
    return parseVTT(text);
  } else if (lowerName.endsWith('.srt')) {
    const vtt = parseSRT(text);
    return parseVTT(vtt);
  }
  throw new Error('Unsupported subtitle format. Use .srt or .vtt files.');
}

/** Get the active subtitle cue for a given time */
export function getActiveCue(cues: SubtitleCue[], time: number): string | null {
  const cue = cues.find((c) => time >= c.startTime && time <= c.endTime);
  return cue?.text ?? null;
}
